/**
 * Renovation Control Page - Phase 4
 * Real-time renovation tracking with financial impact analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Wrench,
  CurrencyEur,
  Clock,
  TrendUp,
  TrendDown,
  Warning,
  CheckCircle,
  ArrowsClockwise,
  Calendar,
  Percent,
  ChartLine,
  Lightning,
  ShieldWarning,
  CaretUp,
  CaretDown,
  Minus,
} from '@phosphor-icons/react';
import { LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { dealsApi, renovationApi } from '../utils/api';
import {
  formatCurrency,
  formatPercent,
  formatArea,
  formatPricePerSqm,
  formatDate,
  getRoiColorClass,
  getProfitColorClass,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Category display names
const CATEGORY_LABELS = {
  demolition: 'Demolition',
  structural_work: 'Structural Work',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  flooring: 'Flooring',
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  painting: 'Painting',
  design_styling: 'Design & Styling',
  project_management: 'Project Management',
  other: 'Other',
};

const VarianceIndicator = ({ value, isPercent = false, inverse = false }) => {
  if (value === 0 || value === null || value === undefined) {
    return <span className="text-muted-foreground flex items-center gap-1"><Minus size={14} /> —</span>;
  }
  
  // For costs: positive = bad (over budget), negative = good (under budget)
  // inverse reverses this logic
  const isNegative = value < 0;
  const isGood = inverse ? !isNegative : isNegative;
  
  return (
    <span className={cn(
      'flex items-center gap-1 font-medium',
      isGood ? 'text-emerald-600' : 'text-red-600'
    )}>
      {isNegative ? <CaretDown size={14} weight="fill" /> : <CaretUp size={14} weight="fill" />}
      {isPercent ? `${Math.abs(value).toFixed(1)}%` : formatCurrency(Math.abs(value))}
    </span>
  );
};

const AlertBadge = ({ alert }) => {
  const severityConfig = {
    warning: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Warning },
    critical: { color: 'bg-red-100 text-red-800 border-red-200', icon: ShieldWarning },
  };
  
  const config = severityConfig[alert.severity] || severityConfig.warning;
  const Icon = config.icon;
  
  return (
    <div className={cn('flex items-start gap-2 p-3 rounded-lg border', config.color)}>
      <Icon size={18} weight="fill" className="mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium text-sm">{alert.message}</p>
        {alert.value !== null && alert.threshold !== null && (
          <p className="text-xs opacity-75 mt-0.5">
            Value: {alert.value?.toFixed?.(1) || alert.value} | Threshold: {alert.threshold?.toFixed?.(1) || alert.threshold}
          </p>
        )}
      </div>
    </div>
  );
};

export const RenovationControl = () => {
  const { dealId: dealIdFromUrl } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deal, setDeal] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [error, setError] = useState(null);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(dealIdFromUrl || null);
  
  // Actuals input state
  const [showActualsInput, setShowActualsInput] = useState(false);
  const [savingActuals, setSavingActuals] = useState(false);
  const [actualSpendInput, setActualSpendInput] = useState('');
  const [actualStartDateInput, setActualStartDateInput] = useState('');
  const [actualEndDateInput, setActualEndDateInput] = useState('');

  // Fetch list of deals for selector
  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const allDeals = await dealsApi.getAll();
        // Filter to deals that are in renovation stage (acquired, construction, ready_for_sale)
        const renovationDeals = allDeals.filter(d => 
          ['acquired', 'construction', 'ready_for_sale', 'active_sale'].includes(d.stage)
        );
        setDeals(renovationDeals.length > 0 ? renovationDeals : allDeals);
        
        // If no deal selected and deals exist, select first one
        if (!selectedDealId && renovationDeals.length > 0) {
          setSelectedDealId(renovationDeals[0].id);
        } else if (!selectedDealId && allDeals.length > 0) {
          setSelectedDealId(allDeals[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch deals:', err);
      }
    };
    fetchDeals();
  }, [selectedDealId]);

  const fetchData = useCallback(async () => {
    if (!selectedDealId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [dealData, trackingData] = await Promise.all([
        dealsApi.getById(selectedDealId),
        renovationApi.getTracking(selectedDealId),
      ]);
      setDeal(dealData);
      setTracking(trackingData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedDealId]);

  useEffect(() => {
    if (selectedDealId) {
      fetchData();
    }
  }, [fetchData, selectedDealId]);

  const handleDealChange = (newDealId) => {
    setSelectedDealId(newDealId);
    // Optionally update URL
    if (newDealId) {
      navigate(`/deal/${newDealId}/renovation`, { replace: true });
    }
  };

  const handleRefresh = async () => {
    if (!selectedDealId) return;
    setRefreshing(true);
    try {
      const result = await renovationApi.recalculate(selectedDealId);
      setTracking(result.tracking);
      if (result.deal) setDeal(result.deal);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle saving actuals
  const handleSaveActuals = async () => {
    if (!selectedDealId) return;
    
    setSavingActuals(true);
    try {
      const data = {
        actual_spend: actualSpendInput ? parseFloat(actualSpendInput) : undefined,
        actual_start_date: actualStartDateInput || undefined,
        actual_end_date: actualEndDateInput || undefined,
      };
      
      const result = await renovationApi.updateActuals(selectedDealId, data);
      setTracking(result.tracking);
      if (result.deal) setDeal(result.deal);
      
      toast.success('Données de rénovation mises à jour');
      setShowActualsInput(false);
    } catch (err) {
      console.error('Save actuals failed:', err);
      toast.error('Échec de l\'enregistrement: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingActuals(false);
    }
  };

  // Initialize actuals inputs from tracking data
  useEffect(() => {
    if (tracking) {
      setActualSpendInput(tracking.actual_total_spend || '');
      setActualStartDateInput(tracking.actual_start_date || '');
      setActualEndDateInput(tracking.actual_end_date || '');
    }
  }, [tracking]);

  // No deals available
  if (!loading && deals.length === 0) {
    return (
      <EmptyState
        icon={Warning}
        title="Aucun deal trouvé"
        description="Créez d'abord un deal pour suivre la rénovation."
        actionLabel="Aller au Pipeline"
        onAction={() => navigate('/pipeline')}
      />
    );
  }

  // Deal selector header (always shown)
  const DealSelector = () => (
    <div className="flex items-center gap-4 mb-6">
      <label className="text-sm font-medium text-muted-foreground">Sélectionner un deal:</label>
      <select
        value={selectedDealId || ''}
        onChange={(e) => handleDealChange(e.target.value)}
        className="px-3 py-2 border border-border rounded-lg bg-background text-foreground min-w-[250px]"
        data-testid="deal-selector"
      >
        <option value="">-- Sélectionner un deal --</option>
        {deals.map(d => (
          <option key={d.id} value={d.id}>
            {d.name} ({d.stage})
          </option>
        ))}
      </select>
    </div>
  );

  if (loading) return (
    <div className="space-y-4">
      <DealSelector />
      <LoadingPage message="Chargement du suivi rénovation..." />
    </div>
  );

  if (error || !deal || !tracking) {
    return (
      <div className="space-y-4">
        <DealSelector />
        <EmptyState
          icon={Warning}
          title="Données non trouvées"
          description={error || "Impossible de charger les données de rénovation. Assurez-vous que le deal a des données de rénovation configurées."}
          actionLabel="Essayer un autre deal"
          onAction={() => setSelectedDealId(null)}
        />
      </div>
    );
  }

  const { impact, alerts, cost_breakdown } = tracking;
  const hasOverrun = tracking.cost_variance > 0;
  const hasDelay = tracking.timeline_delay_days > 0;
  const budgetProgress = tracking.actual_total_spend > 0 
    ? Math.min(100, (tracking.actual_total_spend / tracking.planned_budget) * 100)
    : 0;

  return (
    <div data-testid="renovation-control" className="space-y-6 animate-fade-in">
      {/* Deal Selector */}
      <DealSelector />

      {/* Back Button */}
      <div className="flex items-center justify-between">
        <Link to={`/deal/${selectedDealId}`}>
          <Button variant="ghost" size="sm" data-testid="back-to-deal">
            <ArrowLeft size={16} className="mr-1" />
            Retour au détail
          </Button>
        </Link>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh} 
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <ArrowsClockwise size={16} className={cn("mr-1", refreshing && "animate-spin")} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </Button>
      </div>

      {/* Header */}
      <div className="card-premium" data-testid="reno-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wrench size={28} />
              Contrôle Rénovation
            </h1>
            <p className="text-muted-foreground mt-1">{tracking.deal_name}</p>
            <p className="text-sm text-muted-foreground">{formatArea(tracking.surface_sqm)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge 
              variant="secondary" 
              className={cn(
                'text-sm px-3 py-1',
                tracking.renovation_status === 'completed' && 'bg-emerald-100 text-emerald-700',
                tracking.renovation_status === 'in_progress' && 'bg-blue-100 text-blue-700',
                tracking.renovation_status === 'not_started' && 'bg-stone-100 text-stone-700'
              )}
            >
              {tracking.renovation_status === 'in_progress' ? 'En cours' : 
               tracking.renovation_status === 'completed' ? 'Terminé' : 'Non démarré'}
            </Badge>
            <Badge 
              variant="secondary" 
              className={cn(
                'text-sm px-3 py-1',
                tracking.timeline_status === 'delayed' && 'bg-red-100 text-red-700',
                tracking.timeline_status === 'on_track' && 'bg-emerald-100 text-emerald-700',
                tracking.timeline_status === 'ahead' && 'bg-cyan-100 text-cyan-700'
              )}
            >
              {tracking.timeline_status === 'delayed' ? 'En retard' :
               tracking.timeline_status === 'ahead' ? 'En avance' : 'Dans les temps'}
            </Badge>
          </div>
        </div>
      </div>

      {/* ===== ACTUALS INPUT SECTION ===== */}
      <Collapsible open={showActualsInput} onOpenChange={setShowActualsInput}>
        <Card className="border-2 border-dashed border-[#3A4A40]/30">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-secondary/30 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CurrencyEur size={18} />
                    Saisir les données réelles
                  </CardTitle>
                  <CardDescription>Enregistrer les coûts et dates réels</CardDescription>
                </div>
                <Button variant="outline" size="sm" data-testid="toggle-actuals-input">
                  {showActualsInput ? 'Masquer' : 'Développer'}
                </Button>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="actual_spend">Dépenses réelles (€)</Label>
                  <Input
                    id="actual_spend"
                    type="number"
                    data-testid="input-actual-spend"
                    value={actualSpendInput}
                    onChange={(e) => setActualSpendInput(e.target.value)}
                    placeholder={`Prévu: ${formatCurrency(tracking.planned_budget)}`}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="actual_start_date">Date de début réelle</Label>
                  <Input
                    id="actual_start_date"
                    type="date"
                    data-testid="input-actual-start"
                    value={actualStartDateInput}
                    onChange={(e) => setActualStartDateInput(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="actual_end_date">Date de fin réelle</Label>
                  <Input
                    id="actual_end_date"
                    type="date"
                    data-testid="input-actual-end"
                    value={actualEndDateInput}
                    onChange={(e) => setActualEndDateInput(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowActualsInput(false)}
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleSaveActuals}
                  disabled={savingActuals}
                  className="bg-[#3A4A40] hover:bg-[#2E3B33]"
                  data-testid="save-actuals-btn"
                >
                  {savingActuals ? (
                    <>
                      <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-1" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ===== SECTION 1: TOP SUMMARY ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3" data-testid="summary-cards">
        <Card className="col-span-1">
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Budget Prévu</p>
            <p className="text-lg sm:text-xl font-bold font-tabular">{formatCurrency(tracking.planned_budget)}</p>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Dépenses Réelles</p>
            <p className="text-lg sm:text-xl font-bold font-tabular">{formatCurrency(tracking.actual_total_spend)}</p>
          </CardContent>
        </Card>
        <Card className={cn("col-span-1", hasOverrun ? "border-red-200 bg-red-50/30" : "")}>
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Écart</p>
            <div className="flex justify-center">
              <VarianceIndicator value={tracking.cost_variance} />
            </div>
          </CardContent>
        </Card>
        <Card className={cn("col-span-1", hasOverrun ? "border-red-200 bg-red-50/30" : "")}>
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Écart %</p>
            <div className="flex justify-center">
              <VarianceIndicator value={tracking.cost_variance_percent} isPercent />
            </div>
          </CardContent>
        </Card>
        <Card className={cn("col-span-1", hasDelay ? "border-amber-200 bg-amber-50/30" : "")}>
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Jours de Retard</p>
            <p className={cn(
              'text-lg sm:text-xl font-bold font-tabular',
              hasDelay ? 'text-red-600' : tracking.timeline_delay_days < 0 ? 'text-emerald-600' : ''
            )}>
              {tracking.timeline_delay_days > 0 ? '+' : ''}{tracking.timeline_delay_days}
            </p>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">ROI Actualisé</p>
            <p className={cn('text-lg sm:text-xl font-bold font-tabular', getRoiColorClass(impact?.updated_roi_percent))}>
              {formatPercent(impact?.updated_roi_percent)}
            </p>
          </CardContent>
        </Card>
        <Card className="col-span-2 sm:col-span-1">
          <CardContent className="pt-3 sm:pt-4 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1">Profit Actualisé</p>
            <p className={cn('text-lg sm:text-xl font-bold font-tabular', getProfitColorClass(impact?.updated_net_profit))}>
              {formatCurrency(impact?.updated_net_profit)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Progress Bar */}
      <Card data-testid="budget-progress">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Utilisation du Budget</span>
            <span className="text-sm text-muted-foreground">
              {formatCurrency(tracking.actual_total_spend)} / {formatCurrency(tracking.planned_budget)} ({budgetProgress.toFixed(0)}%)
            </span>
          </div>
          <Progress 
            value={budgetProgress} 
            className={cn("h-3", budgetProgress > 100 && "[&>div]:bg-red-500")} 
          />
          {tracking.contingency_exhausted && (
            <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
              <Warning size={14} weight="fill" />
              Contingence épuisée - tous les dépassements impactent directement le profit
            </p>
          )}
          {!tracking.contingency_exhausted && tracking.contingency_remaining > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Contingence restante: {formatCurrency(tracking.contingency_remaining)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* ===== SECTION 5: ALERTS / FLAGS ===== */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-2" data-testid="alerts-section">
          <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Lightning size={16} />
            Alertes Actives ({alerts.length})
          </h3>
          <div className="grid gap-2">
            {alerts.map((alert, idx) => (
              <AlertBadge key={alert.id || idx} alert={alert} />
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ===== SECTION 2: COST BREAKDOWN TABLE ===== */}
        <Card data-testid="cost-breakdown">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CurrencyEur size={20} />
              Répartition des Coûts par Catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cost_breakdown ? (
              <>
                {/* Mobile: Card Layout */}
                <div className="lg:hidden space-y-2">
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                    const item = cost_breakdown[key];
                    if (!item || (item.planned_cost === 0 && item.actual_cost === 0)) return null;
                    
                    const variance = item.actual_cost - item.planned_cost;
                    const variancePct = item.planned_cost > 0 ? (variance / item.planned_cost) * 100 : 0;
                    const isOverrun = variance > 0;
                    const isUnder = variance < 0;
                    
                    return (
                      <div 
                        key={key}
                        className={cn(
                          "p-3 rounded-lg border",
                          isOverrun && "bg-red-50/50 border-red-200",
                          isUnder && "bg-emerald-50/30 border-emerald-200"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{label}</span>
                          <VarianceIndicator value={variance} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Prévu:</span>
                            <span className="font-tabular ml-1">{formatCurrency(item.planned_cost)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Réel:</span>
                            <span className="font-tabular ml-1">{formatCurrency(item.actual_cost)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Totals card */}
                  <div className="p-3 rounded-lg bg-muted/50 border-2 border-foreground/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-sm">TOTAL</span>
                      <VarianceIndicator value={tracking.cost_variance} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Prévu:</span>
                        <span className="font-tabular font-bold ml-1">{formatCurrency(tracking.planned_budget)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Réel:</span>
                        <span className="font-tabular font-bold ml-1">{formatCurrency(tracking.actual_total_spend)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop: Table Layout */}
                <Table className="hidden lg:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
                      const item = cost_breakdown[key];
                      if (!item || (item.planned_cost === 0 && item.actual_cost === 0)) return null;
                      
                      const variance = item.actual_cost - item.planned_cost;
                      const variancePct = item.planned_cost > 0 ? (variance / item.planned_cost) * 100 : 0;
                      const isOverrun = variance > 0;
                      const isUnder = variance < 0;
                      
                      return (
                        <TableRow 
                          key={key}
                          className={cn(
                            isOverrun && "bg-red-50/50",
                            isUnder && "bg-emerald-50/30"
                          )}
                        >
                          <TableCell className="font-medium">{label}</TableCell>
                          <TableCell className="text-right font-tabular">{formatCurrency(item.planned_cost)}</TableCell>
                          <TableCell className="text-right font-tabular">{formatCurrency(item.actual_cost)}</TableCell>
                          <TableCell className="text-right">
                            <VarianceIndicator value={variance} />
                          </TableCell>
                          <TableCell className="text-right">
                            <VarianceIndicator value={variancePct} isPercent />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals row */}
                    <TableRow className="font-bold border-t-2">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right font-tabular">{formatCurrency(tracking.planned_budget)}</TableCell>
                      <TableCell className="text-right font-tabular">{formatCurrency(tracking.actual_total_spend)}</TableCell>
                      <TableCell className="text-right">
                        <VarianceIndicator value={tracking.cost_variance} />
                      </TableCell>
                      <TableCell className="text-right">
                        <VarianceIndicator value={tracking.cost_variance_percent} isPercent />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            ) : (
              <p className="text-muted-foreground text-center py-4">
                No cost breakdown data available. Update renovation actuals to see breakdown.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ===== SECTION 3: TIMELINE TRACKER ===== */}
        <Card data-testid="timeline-tracker">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={20} />
              Timeline Tracker
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Planned */}
              <div className="p-4 bg-secondary/30 rounded-lg">
                <h4 className="font-semibold text-sm mb-3">PLANNED</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date</span>
                    <span className="font-medium">{formatDate(tracking.planned_start_date) || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date</span>
                    <span className="font-medium">{formatDate(tracking.planned_end_date) || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{tracking.planned_duration_days ? `${tracking.planned_duration_days} days` : '—'}</span>
                  </div>
                </div>
              </div>
              
              {/* Actual */}
              <div className={cn(
                "p-4 rounded-lg",
                hasDelay ? "bg-red-50 border border-red-200" : "bg-secondary/30"
              )}>
                <h4 className="font-semibold text-sm mb-3">ACTUAL</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Start Date</span>
                    <span className="font-medium">{formatDate(tracking.actual_start_date) || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Date</span>
                    <span className="font-medium">{formatDate(tracking.actual_end_date) || 'In Progress'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-medium">{tracking.actual_duration_days ? `${tracking.actual_duration_days} days` : '—'}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Delay Indicator */}
            <div className={cn(
              'p-4 rounded-lg text-center',
              hasDelay ? 'bg-red-100 border border-red-200' :
              tracking.timeline_delay_days < 0 ? 'bg-emerald-100 border border-emerald-200' :
              'bg-stone-100'
            )}>
              <div className="flex items-center justify-center gap-2">
                <Clock size={24} weight={hasDelay ? "fill" : "regular"} className={hasDelay ? 'text-red-600' : ''} />
                <span className="text-lg font-bold">
                  {hasDelay ? `${tracking.timeline_delay_days} days behind schedule` :
                   tracking.timeline_delay_days < 0 ? `${Math.abs(tracking.timeline_delay_days)} days ahead of schedule` :
                   'On Schedule'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ===== SECTION 4: IMPACT PANEL ===== */}
      <Card data-testid="impact-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ChartLine size={20} />
            Financial Impact Analysis
          </CardTitle>
          <CardDescription>
            How renovation deviations affect deal profitability
          </CardDescription>
        </CardHeader>
        <CardContent>
          {impact && (impact.budget_overrun > 0 || impact.delay_days > 0) ? (
            <div className="space-y-6">
              {/* Impact Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {impact.budget_overrun > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">Budget Overrun</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(impact.budget_overrun)}
                    </p>
                    <p className="text-xs text-red-600">
                      {impact.budget_overrun_percent.toFixed(1)}% over planned
                    </p>
                  </div>
                )}
                
                {impact.delay_days > 0 && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-600 font-medium">Timeline Delay</p>
                    <p className="text-2xl font-bold text-amber-700">
                      {impact.delay_days} days
                    </p>
                    <p className="text-xs text-amber-600">
                      Additional interest: {formatCurrency(impact.additional_interest_cost)}
                    </p>
                  </div>
                )}
                
                <div className="p-4 bg-stone-100 border rounded-lg">
                  <p className="text-sm text-muted-foreground font-medium">Total Additional Cost</p>
                  <p className="text-2xl font-bold text-stone-800">
                    {formatCurrency(impact.total_additional_cost)}
                  </p>
                </div>
              </div>
              
              {/* Before/After Comparison */}
              <div>
                <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                  Impact on Deal Metrics
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="p-4 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Total Project Cost</p>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground line-through text-sm">
                        {formatCurrency(impact.original_total_project_cost)}
                      </span>
                      <span className="text-lg font-bold">
                        {formatCurrency(impact.updated_total_project_cost)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Net Profit</p>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground line-through text-sm">
                        {formatCurrency(impact.original_net_profit)}
                      </span>
                      <span className={cn('text-lg font-bold', getProfitColorClass(impact.updated_net_profit))}>
                        {formatCurrency(impact.updated_net_profit)}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      -{formatCurrency(impact.profit_reduction)}
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">ROI</p>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground line-through text-sm">
                        {formatPercent(impact.original_roi_percent)}
                      </span>
                      <span className={cn('text-lg font-bold', getRoiColorClass(impact.updated_roi_percent))}>
                        {formatPercent(impact.updated_roi_percent)}
                      </span>
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      -{impact.roi_reduction_points.toFixed(2)} points
                    </p>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Break-even Price</p>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground line-through text-sm">
                        {formatCurrency(impact.original_break_even_price)}
                      </span>
                      <span className="text-lg font-bold">
                        {formatCurrency(impact.updated_break_even_price)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Key Message */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Warning size={24} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Impact Summary</p>
                    <ul className="text-sm text-amber-700 mt-1 space-y-1">
                      {impact.budget_overrun > 0 && (
                        <li>You are <strong>{formatCurrency(impact.budget_overrun)}</strong> over budget</li>
                      )}
                      {impact.delay_days > 0 && (
                        <li>You are <strong>{impact.delay_days} days</strong> delayed</li>
                      )}
                      <li>This reduces profit by <strong>{formatCurrency(impact.profit_reduction)}</strong></li>
                      <li>This reduces ROI from <strong>{formatPercent(impact.original_roi_percent)}</strong> to <strong>{formatPercent(impact.updated_roi_percent)}</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 py-8 text-emerald-600">
              <CheckCircle size={32} weight="fill" />
              <div>
                <p className="font-semibold text-lg">No negative impact detected</p>
                <p className="text-sm text-muted-foreground">
                  Renovation is on track or under budget
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="renovation-control" 
        content={pageExplanations.renovationControl} 
      />
    </div>
  );
};

export default RenovationControl;
